// Los selectores reales de la app, montados para el Chat —
// ADR-2026-09-08-adjuntar-desde-el-catalogo-de-la-marca.
//
// El "+" del Chat sólo dejaba subir archivos: de los productos, modelos, poses y fondos
// que el usuario ya tiene cargados en su marca no se podía elegir nada. Acá se montan LOS
// MISMOS componentes que usan "Crear desde cero" y los modos —`ProductFullMenu`,
// `ModelPickerSheet`, `PosePickerSheet`, `FondoPickerSheet`, `GeneratedImagesPicker`—,
// así el Chat hereda buscador, orden, "últimos utilizados", subir un modelo o una pose,
// y las mismas dos formas (bottom sheet en mobile, panel a la derecha en desktop).
//
// Lo que sale de cada uno entra como adjunto del motor: producto y modelo tienen su
// propio `kind`; una pose o un fondo entran como `kind: 'referencia'` (su URL viaja
// etiquetada al modelo y como `reference_images` a la generación); una imagen ya generada
// entra como `kind: 'image'` del bucket `generadas`.
//
// Poses y fondos se piden RECIÉN al abrir su selector: son dos catálogos que el Chat no
// necesita para conversar, y pedirlos al montar sería tráfico en cada sesión.
//
// Los adjuntos se arman con `bibliotecaDeAdjuntos.ts`, el mismo módulo que usa la
// biblioteca de la pantalla dedicada: ids estables, así lo que se engancha desde un lado
// se reconoce desde el otro y adjuntar dos veces lo mismo no lo duplica.

import React, { useEffect, useState } from 'react';
import { ProductFullMenu } from '../../pages/v2/ChatPanelV2';
import GeneratedImagesPicker, { type GeneratedPick } from '../../pages/v2/GeneratedImagesPicker';
import { ModelPickerSheet, modelSelectionLabel, type ModelSelection } from '../../pages/v2/ModelPicker';
import { PosePickerSheet } from '../../pages/v2/PosePicker';
import { FondoPickerSheet, type FondoSelection } from '../../pages/v2/FondoPicker';
import { fetchAllPoses, type PoseV2 } from '../../pages/v2/posesApi';
import {
  fetchBibliotecaFondos,
  fetchFondosGuardados,
  type FondoBiblioteca,
  type FondoGuardado,
} from '../../pages/v2/fondosApi';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import type { MockProductV2 } from '../../pages/v2/mockData';
import type { ModelV2 } from '../../pages/v2/modelsApi';
import type { AccionMenuMas } from './menuMasItems';
import {
  adjuntoDeFondoDeBiblioteca,
  adjuntoDeFondoGuardado,
  adjuntoDeGenerada,
  adjuntoDePose,
  adjuntoDeProducto,
} from './bibliotecaDeAdjuntos';

/** Cuál está abierto. `archivos` no llega acá (lo maneja el input de la barra). */
export type SelectorAbierto = Exclude<AccionMenuMas, 'archivos'> | null;

interface Props {
  abierto: SelectorAbierto;
  onCerrar: () => void;
  onAdjuntar: (a: ChatAttachment) => void;
  productos: MockProductV2[];
  modelos: ModelV2[];
  usuarioId: string | null;
  companyId: string | null;
  onModeloCreado: () => void;
}

export const SelectoresDeAdjuntos: React.FC<Props> = ({
  abierto,
  onCerrar,
  onAdjuntar,
  productos,
  modelos,
  usuarioId,
  companyId,
  onModeloCreado,
}) => {
  const [poses, setPoses] = useState<PoseV2[]>([]);
  const [biblioteca, setBiblioteca] = useState<FondoBiblioteca[]>([]);
  const [guardados, setGuardados] = useState<FondoGuardado[]>([]);

  // Carga perezosa: cada catálogo se pide la primera vez que se abre su selector.
  useEffect(() => {
    if (abierto !== 'pose' || poses.length > 0) return;
    let vivo = true;
    void fetchAllPoses()
      .then((p) => {
        if (vivo) setPoses(p);
      })
      .catch(() => {
        /* sin poses el sheet muestra su vacío */
      });
    return () => {
      vivo = false;
    };
  }, [abierto, poses.length]);

  const cargarFondos = React.useCallback(() => {
    void Promise.all([
      fetchBibliotecaFondos(null),
      companyId ? fetchFondosGuardados(companyId) : Promise.resolve([] as FondoGuardado[]),
    ])
      .then(([bib, guard]) => {
        setBiblioteca(bib);
        setGuardados(guard);
      })
      .catch(() => {
        /* sin fondos el sheet muestra su vacío */
      });
  }, [companyId]);

  useEffect(() => {
    if (abierto !== 'fondo' || biblioteca.length > 0) return;
    cargarFondos();
  }, [abierto, biblioteca.length, cargarFondos]);

  const adjuntarProductos = (ids: string[]) => {
    for (const id of ids) {
      const p = productos.find((x) => String(x.id) === String(id));
      if (!p) continue;
      onAdjuntar(adjuntoDeProducto(p));
    }
    onCerrar();
  };

  const adjuntarModelo = (sel: ModelSelection) => {
    onAdjuntar({
      kind: 'model',
      // Un chip por modelo guardado (`model-<id>`): se pueden adjuntar varios
      // (ADR-2026-09-13-varios-modelos-en-la-caja). Una selección sin id (descripta,
      // subida) no tiene con qué distinguirse: conserva el lugar único.
      id: sel.mode === 'preset' ? `model-${sel.id}` : 'model-attachment',
      selection: sel,
      name: modelSelectionLabel(sel),
      imageUrl:
        sel.mode === 'preset' ? sel.imageUrl : sel.mode === 'upload' ? sel.previewUrl : null,
    });
    onCerrar();
  };

  const adjuntarPoses = (ids: string[]) => {
    for (const id of ids) {
      const p = poses.find((x) => String(x.id) === String(id));
      if (!p?.imageUrl) continue;
      onAdjuntar(adjuntoDePose(p));
    }
    onCerrar();
  };

  const adjuntarFondo = (sel: FondoSelection) => {
    // Sólo los fondos que son una IMAGEN sirven como referencia. "Automático", "Fondo
    // blanco" y "Descripción" no son un archivo: son instrucciones, y para eso el
    // usuario ya tiene el prompt.
    if (sel.mode === 'biblioteca') {
      onAdjuntar(adjuntoDeFondoDeBiblioteca({ id: sel.id, nombre: sel.name, imageUrl: sel.imageUrl }));
    } else if (sel.mode === 'guardado') {
      onAdjuntar(
        adjuntoDeFondoGuardado({ fileName: sel.fileName, nombre: sel.name, imageUrl: sel.imageUrl }),
      );
    }
    onCerrar();
  };

  const adjuntarGeneradas = (picks: GeneratedPick[]) => {
    picks.forEach((pick) => onAdjuntar(adjuntoDeGenerada(pick)));
    onCerrar();
  };

  return (
    <>
      {abierto === 'producto' && (
        <ProductFullMenu
          initialSelected={[]}
          allowMulti
          onCancel={onCerrar}
          onAccept={adjuntarProductos}
        />
      )}

      <ModelPickerSheet
        open={abierto === 'modelo'}
        selection={null}
        onChange={adjuntarModelo}
        onClose={onCerrar}
        userModels={modelos}
        userId={usuarioId}
        companyId={companyId}
        onModelCreated={onModeloCreado}
        hideAuto
      />

      <PosePickerSheet
        open={abierto === 'pose'}
        poses={poses}
        selectedIds={[]}
        onChange={adjuntarPoses}
        onClose={onCerrar}
      />

      <FondoPickerSheet
        open={abierto === 'fondo'}
        selection={null}
        onChange={adjuntarFondo}
        onClose={onCerrar}
        biblioteca={biblioteca}
        guardados={guardados}
        companyId={companyId}
        onFondoSaved={cargarFondos}
      />

      <GeneratedImagesPicker
        incluirVideos
        open={abierto === 'generada'}
        companyId={companyId}
        onCancel={onCerrar}
        onAccept={adjuntarGeneradas}
      />
    </>
  );
};

export default SelectoresDeAdjuntos;
